import { useCallback, useRef, useState } from "react";
import { useInView } from "@/shared/hooks/useInView";
import type { ResolvedAdContent } from "@services/media/commands/cmdResolveAds";

export function AdThumb({ resolved, pickedCover }: {
    resolved?: ResolvedAdContent | null;
    /** A just-saved cover, shown before any refetch. The control and the modal live in
     *  the host, not here: this component renders inside a <button> in createEditor, and
     *  a nested button is both invalid and a click that bubbles (MPN-1761). */
    pickedCover?: string | null;
}) {
    const media = resolved?.media;
    const preview = resolved?.preview;
    const isVideo = !!media?.file_type?.startsWith("video");


    const [inViewRef, inView] = useInView("200px");
    const videoRef = useRef<HTMLVideoElement>(null);
    const [previewLoaded, setPreviewLoaded] = useState(false);
    const [mediaLoaded, setMediaLoaded] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [videoLoading, setVideoLoading] = useState(false);

    const togglePlay = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        const vid = videoRef.current;
        if (!vid) return;

        if (playing) {
            vid.pause();
            setPlaying(false);
            return;
        }

        if (vid.readyState < 3) {
            setVideoLoading(true);
            const onCanPlay = () => {
                vid.removeEventListener("canplay", onCanPlay);
                setVideoLoading(false);
                void vid.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
            };
            vid.addEventListener("canplay", onCanPlay);
            vid.load();
            return;
        }

        void vid.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }, [playing]);

    const onKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            togglePlay(e);
        }
    }, [togglePlay]);

    if (resolved && !preview?.storage_uri && !media?.storage_uri) {
        return <div className="ad-card-thumb-inner" />;
    }

    return (
        <div ref={inViewRef} className="ad-thumb-inview">
            {(!inView || (!previewLoaded && !mediaLoaded)) && (
                <div className="ad-thumb-skeleton" />
            )}

            {inView && (
                <>
                    {preview?.storage_uri && !isVideo && (
                        <img
                            src={preview.storage_uri}
                            alt=""
                            onLoad={() => setPreviewLoaded(true)}
                        />
                    )}

                    {media?.storage_uri && !isVideo && (
                        <img
                            src={media.storage_uri}
                            alt=""
                            onLoad={() => setMediaLoaded(true)}
                        />
                    )}

                    {isVideo && (
                        <>
                            <video
                                ref={videoRef}
                                src={media?.storage_uri ?? undefined}
                                // Matches the cover picker, deliberately. Without it this request
                                // caches a response with no CORS headers recorded, and the picker's
                                // crossOrigin request for the SAME url reuses that entry and fails
                                // with "No Access-Control-Allow-Origin header" -- though S3 sends
                                // one (MPN-1761).
                                crossOrigin="anonymous"
                                poster={pickedCover ?? preview?.storage_uri ?? undefined}
                                playsInline
                                preload="metadata"
                                onLoadedData={() => setMediaLoaded(true)}
                                onEnded={() => setPlaying(false)}
                            />
                            <div
                                role="button"
                                tabIndex={0}
                                className={`ad-thumb-play-overlay${playing ? " ad-thumb-play-overlay--playing" : ""}`}
                                onClick={togglePlay}
                                onKeyDown={onKeyDown}
                            >
                                {videoLoading ? (
                                    <span className="ad-thumb-play-spinner" />
                                ) : playing ? (
                                    <span className="ad-thumb-play-icon">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <rect x="4" y="3" width="3" height="10" fill="#333" />
                                            <rect x="9" y="3" width="3" height="10" fill="#333" />
                                        </svg>
                                    </span>
                                ) : (
                                    <span className="ad-thumb-play-icon">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M5.5 3L12.5 8L5.5 13V3Z" fill="#333" />
                                        </svg>
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
